import {
  WS_FRAME_JSON, WS_FRAME_VIEWPORT, WS_FRAME_CURSOR,
  WS_FRAME_STROKE_BEGIN, WS_FRAME_STROKE_APPEND, WS_FRAME_STROKE_COMMIT,
  WS_FRAME_STROKE_ABORT, WS_FRAME_ERASE, WS_FRAME_STROKE_COMMIT_FULL, WS_FRAME_UNERASE,
  type JsonMsg,
} from './protocol.js';

export function encodeJson(msg: JsonMsg): Uint8Array {
  const payload = new TextEncoder().encode(JSON.stringify(msg));
  const out = new Uint8Array(1 + payload.length);
  out[0] = WS_FRAME_JSON;
  out.set(payload, 1);
  return out;
}

export function decodeFrame(buf: ArrayBuffer | Uint8Array): { type: number; rest: Uint8Array } {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  return { type: u8[0] ?? 0, rest: u8.subarray(1) };
}

export function decodeJson<T = JsonMsg>(rest: Uint8Array): T {
  return JSON.parse(new TextDecoder().decode(rest)) as T;
}

export function encodeCursor(x: number, y: number): Uint8Array {
  const buf = new ArrayBuffer(1 + 8);
  const v = new DataView(buf);
  v.setUint8(0, WS_FRAME_CURSOR);
  v.setInt32(1, x | 0, true);
  v.setInt32(5, y | 0, true);
  return new Uint8Array(buf);
}

export function decodeCursor(rest: Uint8Array): { x: number; y: number } {
  const v = new DataView(rest.buffer, rest.byteOffset, rest.byteLength);
  return { x: v.getInt32(0, true), y: v.getInt32(4, true) };
}

export function encodeViewport(x1: number, y1: number, x2: number, y2: number, zoom: number): Uint8Array {
  const buf = new ArrayBuffer(1 + 4 * 4 + 4);
  const v = new DataView(buf);
  v.setUint8(0, WS_FRAME_VIEWPORT);
  v.setInt32(1, x1 | 0, true);
  v.setInt32(5, y1 | 0, true);
  v.setInt32(9, x2 | 0, true);
  v.setInt32(13, y2 | 0, true);
  v.setFloat32(17, zoom, true);
  return new Uint8Array(buf);
}

export function decodeViewport(rest: Uint8Array) {
  const v = new DataView(rest.buffer, rest.byteOffset, rest.byteLength);
  return {
    x1: v.getInt32(0, true), y1: v.getInt32(4, true),
    x2: v.getInt32(8, true), y2: v.getInt32(12, true),
    zoom: v.getFloat32(16, true),
  };
}

function toolToByte(tool: string): number { return tool === 'chalk' ? 1 : 2; }
function byteToTool(b: number): 'chalk' | 'marker' { return b === 1 ? 'chalk' : 'marker'; }
function colorToInt(color: string): number { return parseInt(color.slice(1), 16) & 0xffffff; }
function intToColor(n: number): string { return '#' + n.toString(16).padStart(6, '0'); }

export function encodeStrokeBegin(local_id: number, tool: string, size: number, color: string, x: number, y: number, pressure: number): Uint8Array {
  const buf = new ArrayBuffer(1 + 4 + 1 + 1 + 3 + 4 + 4 + 1);
  const v = new DataView(buf);
  let o = 0;
  v.setUint8(o, WS_FRAME_STROKE_BEGIN); o += 1;
  v.setUint32(o, local_id >>> 0, true); o += 4;
  v.setUint8(o, toolToByte(tool)); o += 1;
  v.setUint8(o, size & 0xff); o += 1;
  const c = colorToInt(color);
  v.setUint8(o, c & 0xff); v.setUint8(o + 1, (c >> 8) & 0xff); v.setUint8(o + 2, (c >> 16) & 0xff); o += 3;
  v.setInt32(o, x | 0, true); o += 4;
  v.setInt32(o, y | 0, true); o += 4;
  v.setUint8(o, Math.round(Math.max(0, Math.min(1, pressure)) * 255));
  return new Uint8Array(buf);
}

export function decodeStrokeBegin(rest: Uint8Array) {
  const v = new DataView(rest.buffer, rest.byteOffset, rest.byteLength);
  let o = 0;
  const local_id = v.getUint32(o, true); o += 4;
  const tool = byteToTool(v.getUint8(o)); o += 1;
  const size = v.getUint8(o); o += 1;
  const c = v.getUint8(o) | (v.getUint8(o + 1) << 8) | (v.getUint8(o + 2) << 16); o += 3;
  const color = intToColor(c);
  const x = v.getInt32(o, true); o += 4;
  const y = v.getInt32(o, true); o += 4;
  const pressure = v.getUint8(o) / 255;
  return { local_id, tool, size, color, x, y, pressure };
}

export function encodeStrokeAppend(local_id: number, pts: { x: number; y: number; pressure: number }[]): Uint8Array {
  const buf = new ArrayBuffer(1 + 4 + 2 + pts.length * 9);
  const v = new DataView(buf);
  let o = 0;
  v.setUint8(o, WS_FRAME_STROKE_APPEND); o += 1;
  v.setUint32(o, local_id >>> 0, true); o += 4;
  v.setUint16(o, pts.length, true); o += 2;
  for (const p of pts) {
    v.setInt32(o, p.x | 0, true); o += 4;
    v.setInt32(o, p.y | 0, true); o += 4;
    v.setUint8(o, Math.round(Math.max(0, Math.min(1, p.pressure)) * 255)); o += 1;
  }
  return new Uint8Array(buf);
}

export function decodeStrokeAppend(rest: Uint8Array) {
  const v = new DataView(rest.buffer, rest.byteOffset, rest.byteLength);
  let o = 0;
  const local_id = v.getUint32(o, true); o += 4;
  const count = v.getUint16(o, true); o += 2;
  const pts: { x: number; y: number; pressure: number }[] = [];
  for (let i = 0; i < count; i++) {
    const x = v.getInt32(o, true); o += 4;
    const y = v.getInt32(o, true); o += 4;
    const p = v.getUint8(o) / 255; o += 1;
    pts.push({ x, y, pressure: p });
  }
  return { local_id, points: pts };
}

export function encodeStrokeCommit(local_id: number): Uint8Array {
  const buf = new ArrayBuffer(1 + 4);
  const v = new DataView(buf);
  v.setUint8(0, WS_FRAME_STROKE_COMMIT);
  v.setUint32(1, local_id >>> 0, true);
  return new Uint8Array(buf);
}
export function decodeStrokeCommit(rest: Uint8Array) {
  const v = new DataView(rest.buffer, rest.byteOffset, rest.byteLength);
  return { local_id: v.getUint32(0, true) };
}

/** v2 提交式：整笔一次上行（type | local_id u32 | tool u8 | size u8 | color 3B | count u16 | points×9B） */
export function encodeStrokeCommitFull(
  local_id: number,
  tool: string,
  size: number,
  color: string,
  pts: { x: number; y: number; pressure: number }[],
): Uint8Array {
  const buf = new ArrayBuffer(1 + 4 + 1 + 1 + 3 + 2 + pts.length * 9);
  const v = new DataView(buf);
  let o = 0;
  v.setUint8(o, WS_FRAME_STROKE_COMMIT_FULL); o += 1;
  v.setUint32(o, local_id >>> 0, true); o += 4;
  v.setUint8(o, toolToByte(tool)); o += 1;
  v.setUint8(o, size & 0xff); o += 1;
  const c = colorToInt(color);
  v.setUint8(o, c & 0xff); v.setUint8(o + 1, (c >> 8) & 0xff); v.setUint8(o + 2, (c >> 16) & 0xff); o += 3;
  v.setUint16(o, pts.length, true); o += 2;
  for (const p of pts) {
    v.setInt32(o, p.x | 0, true); o += 4;
    v.setInt32(o, p.y | 0, true); o += 4;
    v.setUint8(o, Math.round(Math.max(0, Math.min(1, p.pressure)) * 255)); o += 1;
  }
  return new Uint8Array(buf);
}

export function decodeStrokeCommitFull(rest: Uint8Array) {
  const v = new DataView(rest.buffer, rest.byteOffset, rest.byteLength);
  let o = 0;
  const local_id = v.getUint32(o, true); o += 4;
  const tool = byteToTool(v.getUint8(o)); o += 1;
  const size = v.getUint8(o); o += 1;
  const c = v.getUint8(o) | (v.getUint8(o + 1) << 8) | (v.getUint8(o + 2) << 16); o += 3;
  const color = intToColor(c);
  const count = v.getUint16(o, true); o += 2;
  const points: { x: number; y: number; pressure: number }[] = [];
  for (let i = 0; i < count; i++) {
    const x = v.getInt32(o, true); o += 4;
    const y = v.getInt32(o, true); o += 4;
    const pressure = v.getUint8(o) / 255; o += 1;
    points.push({ x, y, pressure });
  }
  return { local_id, tool, color, size, points };
}

export function encodeStrokeAbort(local_id: number): Uint8Array {
  const buf = new ArrayBuffer(1 + 4);
  const v = new DataView(buf);
  v.setUint8(0, WS_FRAME_STROKE_ABORT);
  v.setUint32(1, local_id >>> 0, true);
  return new Uint8Array(buf);
}
export function decodeStrokeAbort(rest: Uint8Array) {
  const v = new DataView(rest.buffer, rest.byteOffset, rest.byteLength);
  return { local_id: v.getUint32(0, true) };
}

/** stroke id 用 f64 传输：PG bigint id 无符号上限远超 u32，本地/极端序列下
 *  id 可超 42.9 亿（u32 溢出会把 id 截断成错误值——擦除静默失败的来源）。
 *  JS number 在 2^53 内精确，覆盖任何现实 id。 */
export function encodeErase(stroke_ids: number[]): Uint8Array {
  const buf = new ArrayBuffer(1 + 2 + stroke_ids.length * 8);
  const v = new DataView(buf);
  let o = 0;
  v.setUint8(o, WS_FRAME_ERASE); o += 1;
  v.setUint16(o, stroke_ids.length, true); o += 2;
  for (const id of stroke_ids) { v.setFloat64(o, id, true); o += 8; }
  return new Uint8Array(buf);
}
export function decodeErase(rest: Uint8Array) {
  const v = new DataView(rest.buffer, rest.byteOffset, rest.byteLength);
  let o = 0;
  const count = v.getUint16(o, true); o += 2;
  const ids: number[] = [];
  for (let i = 0; i < count; i++) { ids.push(v.getFloat64(o, true)); o += 8; }
  return { stroke_ids: ids };
}

/** 撤销擦除：type | event_id u32 */
export function encodeUnerase(event_id: number): Uint8Array {
  const buf = new ArrayBuffer(1 + 4);
  const v = new DataView(buf);
  v.setUint8(0, WS_FRAME_UNERASE);
  v.setUint32(1, event_id >>> 0, true);
  return new Uint8Array(buf);
}
export function decodeUnerase(rest: Uint8Array) {
  const v = new DataView(rest.buffer, rest.byteOffset, rest.byteLength);
  return { event_id: v.getUint32(0, true) };
}

export { WS_FRAME_JSON };
